"use client";

import { useState, useEffect, useMemo } from "react";
import { useParams } from "next/navigation";
import TFLink from "@/Web/Components/TFLink/TFLink";
import TFLayoutHomeSection from "@/Web/Components/TFLayoutHomeSection/TFLayoutHomeSection";
import apiCall from "@/libs/api";
import { ImBooks } from "react-icons/im";
import { useTranslations } from "next-intl";

interface BookCategoryCardProps {
  title: string;
  description?: string;
  bookCount: number;
  href: string;
}

const BookCategoryCard = ({
  title,
  description,
  bookCount,
  href,
}: BookCategoryCardProps) => {
  const t = useTranslations("web.pages.home.sections.library");

  return (
    <TFLink
      href={href}
      className="group relative overflow-hidden rounded-lg bg-black/5 p-4 transition-all duration-200 hover:bg-black/10 hover:shadow-sm flex flex-col h-full"
    >
      <h3 className="text-base font-semibold text-black line-clamp-2 group-hover:text-black/70 transition-colors mb-2">
        {title}
      </h3>

      {description && (
        <p className="text-sm text-black/60 mb-3 line-clamp-2 flex-grow">
          {description}
        </p>
      )}

      <div className="flex items-center gap-1.5 text-xs text-black/50 mt-auto">
        <ImBooks className="w-4 h-4 text-black/40" />
        <span className="tabular-nums">
          {bookCount} {t("book", { count: bookCount })}
        </span>
      </div>
    </TFLink>
  );
};

export default function SectionLibrary() {
  const t = useTranslations("web.pages.home.sections.library");
  const params = useParams<{ locale: string }>();
  const locale = params?.locale || "en";
  const [categories, setCategories] = useState<APIDataBookCategory[]>([]);
  const [books, setBooks] = useState<APIDataBook[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const booksResponse = await apiCall("books/getBooks", {
          locale: locale as UILocale,
        });

        const categoriesResponse = await apiCall("books/getCategories", {
          locale: locale as UILocale,
        });

        setBooks(booksResponse.books || []);
        setCategories(categoriesResponse.categories || []);
      } catch (error) {
        console.error("Ошибка при загрузке данных:", error);
        setBooks([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [locale]);

  const categoriesWithBooks = useMemo(() => {
    return categories
      .map((category) => {
        const booksInCategory = books.filter(
          (book) => book.category?.ref === category.ref
        );
        return {
          ...category,
          bookCount: booksInCategory.length,
        };
      })
      .filter((cat) => cat.bookCount > 0);
    // .slice(0, 6);
  }, [categories, books]);

  if (loading) {
    return (
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="text-black/50">{t("loading")}</div>
        </div>
      </section>
    );
  }

  return (
    <TFLayoutHomeSection
      // title="Исследуйте библиотеку"
      // description="Откройте для себя богатую коллекцию священных текстов, комментариев и материалов для изучения"
      moreButton={{
        title: t("allLibrary"),
        href: "/library",
      }}
      sideInfo={{
        title: t("title"),
        description: t("description"),
        statistics: [
          { title: t("totalBooks"), value: books.length },
          { title: t("categories"), value: categoriesWithBooks.length },
        ],
        position: "left",
      }}
    >
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3">
        {categoriesWithBooks.map((category) => (
          <BookCategoryCard
            key={category.ref}
            title={category.name.translation}
            description={category.description.translation}
            bookCount={category.bookCount}
            href={`/library?category=${category.ref}`}
          />
        ))}
      </div>
    </TFLayoutHomeSection>
  );
}
